import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Award, ShieldCheck, Wallet, Edit2, Trash2, Calendar, UserCheck, ArrowLeft, Tag, Layers, DollarSign, Activity, Percent, Clock, X as IconX, ChevronLeft, ChevronRight } from 'lucide-react';
import { CustomSelect } from '../../components/CustomSelect';
import { fetchWithAuth } from '../../utils/api';
import { SideDrawer } from '../../components/SideDrawer';
import { CustomDatePickerFixed } from '../../components/CustomDatePickerFixed';
import { IconAction, RowActions } from '../../components/RowActions';

const Field = ({ label, icon: Icon, error, required, children }) => {
  const isCustomComponent = children?.type === CustomSelect || children?.props?.isCustomSelect;
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </span>
      <span className="relative block">
        {Icon && (
          <span className={`absolute left-3 top-1/2 -translate-y-1/2 z-[60] ${error ? 'text-rose-400' : 'text-slate-400'}`} aria-hidden="true">
            <Icon size={14} />
          </span>
        )}
        <span className="block">{React.cloneElement(children, {
          className: isCustomComponent
            ? `${children.props.className || ''} ${Icon ? 'pl-9' : ''}`
            : `${children.props.className || ''} w-full rounded-lg bg-gray-50 border ${error ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-colors ${Icon ? 'pl-9' : ''}`,
        })}</span>
      </span>
      {error && <p className="text-[11px] font-medium text-rose-500 mt-1">{error}</p>}
    </label>
  );
};

export const MembershipsTab = ({ org, showNotification }) => {
  const [activeTab, setActiveTab] = useState('catalog'); // 'catalog' or 'ledger'
  const [searchTerm, setSearchTerm] = useState('');
  
  const [plans, setPlans] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination State
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPlans, setTotalPlans] = useState(0);
  const [totalLedgers, setTotalLedgers] = useState(0);

  // Views: 'list', 'plan_form', 'ledger_form'
  const [viewState, setViewState] = useState('list');
  const [editItem, setEditItem] = useState(null);

  const [planForm, setPlanForm] = useState({ 
    name: '', type: 'VIP_TIER', price: '', validityAmount: '1', validityType: 'months', startDate: '', endDate: '', 
    discountPercent: '', totalSessions: '', bonusCredit: '' 
  });

  const [ledgerForm, setLedgerForm] = useState({ 
    customerId: '', membershipPlanId: '', remainingValue: '',
    startDate: new Date().toISOString().split('T')[0],
    expiryDate: '' 
  });
  const [errors, setErrors] = useState({});

  const fetchData = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const [pRes, lRes, cRes] = await Promise.all([
        fetchWithAuth(`/membershipplans?page=${page}&limit=${perPage}`, { headers: { 'x-org-id': org.id } }),
        fetchWithAuth(`/customermemberships?page=${page}&limit=${perPage}`, { headers: { 'x-org-id': org.id } }),
        fetchWithAuth('/customers', { headers: { 'x-org-id': org.id } })
      ]);
      const pData = await pRes.json();
      const lData = await lRes.json();
      const cData = await cRes.json();
      
      setTotalPlans(parseInt(pRes.headers.get('X-Total-Count') || pData?.length || 0, 10));
      setTotalLedgers(parseInt(lRes.headers.get('X-Total-Count') || lData?.length || 0, 10));

      setPlans(pData || []);
      setLedgers(lData || []);
      setCustomers(cData || []);
    } catch (err) {
      console.error(err);
      showNotification('Failed to fetch data.', 'error');
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    if (org?.id) fetchData();
  }, [org?.id, page, perPage, activeTab]);

  // Reset page when changing tabs
  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const handleTogglePlanStatus = async (plan) => {
    try {
      const newStatus = plan.status === 'active' ? 'inactive' : 'active';
      // Optimistic local update
      setPlans(prev => prev.map(p => p.id === plan.id ? { ...p, status: newStatus } : p));
      
      const payload = { ...plan, status: newStatus };
      await fetchWithAuth(`/membershipplans/${plan.id}`, {
        method: 'PUT',
        headers: { 'x-org-id': org.id, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      showNotification(`Plan marked as ${newStatus}.`, 'success');
      fetchData(false);
    } catch (err) {
      console.error(err);
      showNotification('Error updating plan status.', 'error');
      fetchData(false); // Revert optimistic update
    }
  };

  const handleToggleStatus = async (ledger) => {
    try {
      const newStatus = ledger.status === 'active' ? 'inactive' : 'active';
      // Optimistic local update
      setLedgers(prev => prev.map(l => l.id === ledger.id ? { ...l, status: newStatus } : l));
      
      const payload = { ...ledger, status: newStatus };
      await fetchWithAuth(`/customermemberships/${ledger.id}`, {
        method: 'PUT',
        headers: { 'x-org-id': org.id, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      showNotification(`Ledger marked as ${newStatus}.`, 'success');
      fetchData(false);
    } catch (err) {
      console.error(err);
      showNotification('Error updating ledger status.', 'error');
      fetchData(false); // Revert optimistic update
    }
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    try {
      const newErrors = {};
      if (!planForm.name || !planForm.name.trim()) newErrors.name = 'Please enter a plan name.';
      if (!planForm.price) newErrors.price = 'Please enter a price.';
      if (planForm.type === 'VIP_TIER' && !planForm.discountPercent) newErrors.discountPercent = 'Please enter a discount percentage.';
      if (planForm.type === 'SERVICE_PACKAGE' && !planForm.totalSessions) newErrors.totalSessions = 'Please enter total sessions.';
      if (planForm.type === 'WALLET_CREDIT' && !planForm.bonusCredit) newErrors.bonusCredit = 'Please enter bonus credit.';

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return showNotification('Please fix the highlighted fields.', 'error');
      }
      setErrors({});

      let finalDays = parseInt(planForm.validityAmount) || 0;
      if (planForm.validityType === 'months') finalDays = finalDays * 30;
      if (planForm.validityType === 'years') finalDays = finalDays * 365;

      const payload = {
        name: planForm.name,
        type: planForm.type,
        price: parseFloat(planForm.price) || 0,
        validityDays: finalDays,
        discountPercent: parseFloat(planForm.discountPercent) || 0,
        totalSessions: parseInt(planForm.totalSessions) || 0,
        bonusCredit: parseFloat(planForm.bonusCredit) || 0
      };
      if (planForm.startDate) payload.startDate = new Date(planForm.startDate).toISOString();
      if (planForm.endDate) payload.endDate = new Date(planForm.endDate).toISOString();

      if (editItem) {
        await fetchWithAuth(`/membershipplans/${editItem.id}`, {
          method: 'PUT',
          headers: { 'x-org-id': org.id, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        showNotification('Plan updated successfully.', 'success');
      } else {
        await fetchWithAuth('/membershipplans', {
          method: 'POST',
          headers: { 'x-org-id': org.id, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        showNotification('Plan created successfully.', 'success');
      }
      setViewState('list');
      setEditItem(null);
      fetchData();
    } catch (err) {
      console.error(err);
      showNotification('Error saving plan.', 'error');
    }
  };

  const handleSaveLedger = async (e) => {
    e.preventDefault();
    try {
      const newErrors = {};
      if (!ledgerForm.customerId) newErrors.customerId = 'Please select a customer.';
      if (!ledgerForm.membershipPlanId) newErrors.membershipPlanId = 'Please select a plan.';
      if (!ledgerForm.startDate) newErrors.startDate = 'Start date is required.';
      if (!ledgerForm.expiryDate) newErrors.expiryDate = 'Expiry date is required.';
      if (ledgerForm.remainingValue === '') newErrors.remainingValue = 'Balance is required.';

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return showNotification('Please fix the highlighted fields.', 'error');
      }
      setErrors({});

      const selectedPlan = plans.find(p => p.id === ledgerForm.membershipPlanId);
      if (!selectedPlan && !editItem) return;

      const payload = {
        customerId: ledgerForm.customerId,
        membershipPlanId: ledgerForm.membershipPlanId,
        status: ledgerForm.status || 'active',
        remainingValue: parseFloat(ledgerForm.remainingValue) || 0,
        startDate: ledgerForm.startDate ? new Date(ledgerForm.startDate).toISOString() : null,
        expiryDate: ledgerForm.expiryDate ? new Date(ledgerForm.expiryDate).toISOString() : null
      };

      // Set expiry automatically on creation
      if (!editItem && selectedPlan) {
        const start = new Date();
        const end = new Date();
        end.setDate(end.getDate() + selectedPlan.validityDays);
        payload.startDate = start.toISOString();
        payload.expiryDate = end.toISOString();
        
        // Auto-initialize remaining value based on plan type
        if (selectedPlan.type === 'SERVICE_PACKAGE') payload.remainingValue = selectedPlan.totalSessions;
        if (selectedPlan.type === 'WALLET_CREDIT') payload.remainingValue = selectedPlan.price + selectedPlan.bonusCredit;
      }

      if (editItem) {
        await fetchWithAuth(`/customermemberships/${editItem.id}`, {
          method: 'PUT',
          headers: { 'x-org-id': org.id, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        showNotification('Ledger updated successfully.', 'success');
      } else {
        await fetchWithAuth('/customermemberships', {
          method: 'POST',
          headers: { 'x-org-id': org.id, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        showNotification('Ledger assigned successfully.', 'success');
      }
      setViewState('list');
      setEditItem(null);
      fetchData();
    } catch (err) {
      console.error(err);
      showNotification('Error saving ledger.', 'error');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;
    try {
      await fetchWithAuth(`/${type}s/${id}`, {
        method: 'DELETE',
        headers: { 'x-org-id': org.id }
      });
      showNotification(`${type} deleted successfully.`, 'success');
      fetchData();
    } catch (err) {
      console.error(err);
      showNotification(`Failed to delete ${type}.`, 'error');
    }
  };

  const pagePlans = plans.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const pageLedgers = ledgers.filter(l => (l.customerId || '').toLowerCase().includes(searchTerm.toLowerCase()));

  const activeList = activeTab === 'catalog' ? pagePlans : pageLedgers;
  
  // Calculate total pages from backend total count
  const totalRecords = activeTab === 'catalog' ? totalPlans : totalLedgers;
  const totalPages = Math.max(1, Math.ceil(totalRecords / perPage));

  const getPlanIcon = (type) => {
    switch (type) {
      case 'VIP_TIER': return <Award className="w-5 h-5 text-amber-500" />;
      case 'SERVICE_PACKAGE': return <ShieldCheck className="w-5 h-5 text-blue-500" />;
      case 'WALLET_CREDIT': return <Wallet className="w-5 h-5 text-emerald-500" />;
      default: return <Award className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      {/* Action Header Section */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 shadow-sm">
        <div className="relative flex-1 min-w-[180px] max-w-lg">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            type="search"
            placeholder="Search plans and ledgers..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none transition-colors"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setEditItem(null);
            setErrors({});
            if (activeTab === 'catalog') {
              setPlanForm({ name: '', type: 'VIP_TIER', price: '', validityAmount: '1', validityType: 'months', startDate: '', endDate: '', discountPercent: '', totalSessions: '', bonusCredit: '' });
              setViewState('plan_form');
            } else {
              setLedgerForm({ 
                customerId: '', membershipPlanId: '', remainingValue: '',
                startDate: new Date().toISOString().split('T')[0],
                expiryDate: '' 
              });
              setViewState('ledger_form');
            }
          }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] transition-colors shadow-lg shrink-0"
          style={{ background: 'var(--theme-primary)', boxShadow: '0 12px 24px -12px var(--theme-primary)' }}
        >
          <Plus size={14} aria-hidden="true" /> New {activeTab === 'catalog' ? 'Plan' : 'Ledger'}
        </button>
      </section>

      {viewState === 'list' && (
        <>
          {/* Enterprise Tabs */}
          <div className="border-b border-[var(--theme-tertiary)] mb-2 px-2">
            <nav className="flex space-x-6" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab('catalog')}
                  className={`flex items-center gap-2 py-4 px-4 text-xs font-bold tracking-wider uppercase border-b-2 transition-colors ${
                    activeTab === 'catalog'
                      ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Plan Catalog
                </button>
                <button
                  onClick={() => setActiveTab('ledger')}
                  className={`flex items-center gap-2 py-4 px-4 text-xs font-bold tracking-wider uppercase border-b-2 transition-colors ${
                    activeTab === 'ledger'
                      ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Customer Ledgers
                </button>
              </nav>
            </div>

            {/* Content Area */}
      {loading ? (
        <div className="flex justify-center py-20 items-center">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="animate-in fade-in duration-500 flex flex-col flex-1 min-h-0">
        {activeTab === 'catalog' ? (
          <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden relative shadow-sm flex flex-col min-h-0 w-full flex-1">
            <div className="overflow-auto overscroll-none flex-1 custom-scrollbar min-h-0 table-scrollbar">
            <table className="w-full text-left border-collapse relative">
              <thead className="bg-white sticky top-0 z-10 shadow-[0_1px_0_0_var(--theme-tertiary)]">
                <tr>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Plan Name</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Type</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Price</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Validity</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Benefits</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Status</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--theme-tertiary)]">
                {pagePlans.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-5 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <h3 className="text-sm font-semibold text-slate-600">No membership plans found</h3>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pagePlans.map(plan => (
                    <tr key={plan.id} className="border-b border-[var(--theme-tertiary)] last:border-0 hover:bg-slate-50/50 transition-colors group">
                      <td className="px-5 py-3">
                        <p className="text-[13px] font-extrabold text-[#0d0d0d] tracking-wide truncate">{plan.name}</p>
                      </td>
                      <td className="px-5 py-3">
                        <p className="text-[10px] tracking-wider text-slate-500 uppercase font-semibold">{plan.type.replace('_', ' ')}</p>
                      </td>
                      <td className="px-5 py-3">
                        <p className="text-[14px] font-bold text-[#0d0d0d]">₹{plan.price}</p>
                      </td>
                      <td className="px-5 py-3">
                        {plan.validityDays > 0 ? (
                          <p className="text-[12px] font-medium text-slate-600">{plan.validityDays} days</p>
                        ) : (
                          <p className="text-[12px] font-medium text-slate-400">Unlimited</p>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        {plan.type === 'VIP_TIER' && <span className="text-[12px] font-semibold text-slate-700">{plan.discountPercent}% Global Discount</span>}
                        {plan.type === 'SERVICE_PACKAGE' && <span className="text-[12px] font-semibold text-slate-700">{plan.totalSessions} Prepaid Sessions</span>}
                        {plan.type === 'WALLET_CREDIT' && <span className="text-[12px] font-semibold text-slate-700">₹{plan.price + plan.bonusCredit} Total Credit</span>}
                      </td>
                      <td className="px-5 py-3">
                        <button
                          onClick={() => handleTogglePlanStatus(plan)}
                          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors focus:outline-none ${plan.status === 'active' ? 'bg-green-500' : 'bg-slate-300'}`}
                          title={plan.status === 'active' ? 'Active' : 'Inactive'}
                        >
                          <span className="sr-only">Toggle status</span>
                          <span
                            aria-hidden="true"
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${plan.status === 'active' ? 'translate-x-2' : '-translate-x-2'}`}
                          />
                        </button>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <RowActions>
                          <IconAction
                            icon={Edit2}
                            label={`Edit ${plan.name}`}
                            title="Edit"
                            onClick={() => {
                              setEditItem(plan);
                              setErrors({});
                              let vAmount = plan.validityDays || 0;
                              let vType = 'days';
                              if (vAmount > 0) {
                                if (vAmount >= 365 && vAmount % 365 === 0) { vAmount = vAmount / 365; vType = 'years'; }
                                else if (vAmount >= 30 && vAmount % 30 === 0) { vAmount = vAmount / 30; vType = 'months'; }
                              }
                              setPlanForm({
                                ...plan,
                                validityAmount: vAmount || '',
                                validityType: vType,
                                startDate: plan.startDate ? plan.startDate.split('T')[0] : '',
                                endDate: plan.endDate ? plan.endDate.split('T')[0] : '',
                              });
                              setViewState('plan_form');
                            }}
                          />
                          <IconAction
                            icon={Trash2}
                            tone="danger"
                            label={`Remove ${plan.name}`}
                            title="Remove"
                            onClick={() => handleDelete('membershipplan', plan.id)}
                          />
                        </RowActions>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </section>
        ) : (
          <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden relative shadow-sm flex flex-col min-h-0 w-full flex-1">
            <div className="overflow-auto overscroll-none flex-1 custom-scrollbar min-h-0 table-scrollbar">
            <table className="w-full text-left border-collapse relative">
              <thead className="bg-white sticky top-0 z-10 shadow-[0_1px_0_0_var(--theme-tertiary)]">
                <tr>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Customer ID</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Subscribed Plan</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Balance</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Valid Until</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Status</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--theme-tertiary)]">
                {pageLedgers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-5 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <h3 className="text-sm font-semibold text-slate-600">No ledgers found</h3>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pageLedgers.map(ledger => (
                    <tr key={ledger.id} className="border-b border-[var(--theme-tertiary)] last:border-0 hover:bg-slate-50/50 transition-colors group">
                      <td className="px-5 py-3">
                        <p className="text-[13px] font-extrabold text-[#0d0d0d] tracking-wide truncate">{ledger.customer?.name || ledger.customerId}</p>
                        {ledger.customer?.phone && <p className="text-[11px] text-slate-500 mt-0.5">{ledger.customer.phone}</p>}
                      </td>
                      <td className="px-5 py-3">
                        <p className="text-[13px] font-extrabold text-[#0d0d0d] tracking-wide truncate">{ledger.plan?.name || 'Unknown Plan'}</p>
                      </td>
                      <td className="px-5 py-3">
                        <p className="text-[13px] font-bold text-slate-700">
                          {ledger.plan?.type === 'WALLET_CREDIT' ? `₹${ledger.remainingValue}` : `${ledger.remainingValue} Left`}
                        </p>
                      </td>
                      <td className="px-5 py-3">
                        <p className="text-[12px] font-medium text-slate-600">{ledger.expiryDate ? new Date(ledger.expiryDate).toLocaleDateString() : 'N/A'}</p>
                      </td>
                      <td className="px-5 py-3">
                        <button
                          onClick={() => handleToggleStatus(ledger)}
                          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors focus:outline-none ${ledger.status === 'active' ? 'bg-green-500' : 'bg-slate-300'}`}
                          title={ledger.status === 'active' ? 'Active' : 'Inactive'}
                        >
                          <span className="sr-only">Toggle status</span>
                          <span
                            aria-hidden="true"
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${ledger.status === 'active' ? 'translate-x-2' : '-translate-x-2'}`}
                          />
                        </button>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <RowActions>
                          <IconAction
                            icon={Edit2}
                            label="Edit this membership"
                            title="Edit"
                            onClick={() => {
                              setEditItem(ledger);
                              setErrors({});
                              setLedgerForm({
                                ...ledger,
                                startDate: ledger.startDate ? ledger.startDate.split('T')[0] : '',
                                expiryDate: ledger.expiryDate ? ledger.expiryDate.split('T')[0] : '',
                              });
                              setViewState('ledger_form');
                            }}
                          />
                          <IconAction
                            icon={Trash2}
                            tone="danger"
                            label="Remove this membership"
                            title="Remove"
                            onClick={() => handleDelete('customermembership', ledger.id)}
                          />
                        </RowActions>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </section>
        )}

        {totalPages > 1 && (
          <section className="px-5 py-3 flex items-center justify-between text-[11px] font-bold text-slate-500 border-t border-[var(--theme-tertiary)] bg-white shrink-0">
            <div className="flex items-center gap-2">
              <span className="uppercase tracking-widest">Showing</span>
              <CustomSelect
                value={perPage}
                onChange={(val) => {
                  setPerPage(Number(val));
                  setPage(1);
                }}
                options={[
                  { value: 10, label: '10' },
                  { value: 25, label: '25' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                  { value: activeList.length > 0 ? activeList.length : 200, label: 'All' }
                ]}
                className="w-16 bg-white border border-[var(--theme-tertiary)] rounded-sm px-2 py-1 text-[11px] font-bold text-slate-700 outline-none hover:border-[var(--theme-primary)] transition-colors cursor-pointer"
                menuUp={true}
              />
            </div>
            <div className="flex items-center gap-4">
              <span className="uppercase tracking-widest">
                PAGE <strong className="text-slate-800 mx-0.5">{page}</strong> OF <strong className="text-slate-800 ml-0.5">{totalPages}</strong>
              </span>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 disabled:hover:border-[var(--theme-tertiary)] disabled:hover:text-slate-500 transition-all shadow-sm">
                  <ChevronLeft size={14} />
                </button>
                <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 disabled:hover:border-[var(--theme-tertiary)] disabled:hover:text-slate-500 transition-all shadow-sm">
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </section>
        )}

      </div>
      )}
        </>
      )}

      {/* Plan Inline Form */}
      {viewState === 'plan_form' && (
        <SideDrawer onClose={() => setViewState('list')} maxWidth="max-w-3xl">
          <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: 'var(--theme-primary)' }}>
                <Award size={18} />
              </div>
              <div>
                <h3 className="text-lg font-extrabold text-[#0d0d0d]">{editItem ? 'Edit plan' : 'New plan'}</h3>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Design membership tiers</p>
              </div>
            </div>
            <button type="button" onClick={() => setViewState('list')} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50"><IconX size={16} /></button>
          </header>
          
          <div className="px-6 py-5 flex flex-col gap-4 flex-1 overflow-y-auto custom-scrollbar">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Plan Name" icon={Tag} error={errors.name} required>
                <input type="text" required placeholder="e.g. Gold Membership" value={planForm.name} onChange={e => {
                  setPlanForm({...planForm, name: e.target.value});
                  if (errors.name) setErrors({...errors, name: undefined});
                }} />
              </Field>
              <div className="space-y-1 flex flex-col">
                <Field label="Plan Type" icon={Layers}>
                  <CustomSelect
                    value={planForm.type}
                    onChange={v => setPlanForm({...planForm, type: v})}
                    options={[
                      { value: "VIP_TIER", label: "VIP Tier" },
                      { value: "SERVICE_PACKAGE", label: "Service Package" },
                      { value: "WALLET_CREDIT", label: "Prepaid Wallet" }
                    ]}
                  />
                </Field>
                <div className="min-h-[32px]">
                  {planForm.type === 'VIP_TIER' && <p className="text-[11px] text-gray-500 font-medium pl-1 leading-relaxed">Give customers a flat discount (%) on all their future bills.</p>}
                  {planForm.type === 'SERVICE_PACKAGE' && <p className="text-[11px] text-gray-500 font-medium pl-1 leading-relaxed">Customers buy a bundle of prepaid sessions (e.g., 5 Haircuts).</p>}
                  {planForm.type === 'WALLET_CREDIT' && <p className="text-[11px] text-gray-500 font-medium pl-1 leading-relaxed">Customers load money into their store wallet to spend later.</p>}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Price (₹)" icon={DollarSign} error={errors.price} required>
                <input type="number" step="0.01" required placeholder="e.g. 999" value={planForm.price} onChange={e => {
                  setPlanForm({...planForm, price: e.target.value});
                  if (errors.price) setErrors({...errors, price: undefined});
                }} />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Duration" icon={Calendar}>
                  <input type="number" placeholder="e.g. 1, 3, 6" value={planForm.validityAmount} onChange={e => setPlanForm({...planForm, validityAmount: e.target.value})} />
                </Field>
                <Field label="Duration Unit" icon={Clock}>
                  <CustomSelect
                    value={planForm.validityType}
                    onChange={v => setPlanForm({...planForm, validityType: v})}
                    options={[
                      { value: "days", label: "Days" },
                      { value: "months", label: "Months" },
                      { value: "years", label: "Years" }
                    ]}
                  />
                </Field>
              </div>
            </div>

            <div className="p-5 bg-slate-50 rounded-lg border border-slate-100 min-h-[140px]">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-4 flex items-center gap-2"><Award className="w-4 h-4 text-[var(--theme-primary)]"/> Plan Benefits</h4>
              {planForm.type === 'VIP_TIER' && (
                <Field label="Global Discount (%)" icon={Percent} error={errors.discountPercent} required>
                  <input type="number" step="0.01" required placeholder="e.g. 10" value={planForm.discountPercent} onChange={e => {
                    setPlanForm({...planForm, discountPercent: e.target.value});
                    if (errors.discountPercent) setErrors({...errors, discountPercent: undefined});
                  }} />
                </Field>
              )}
              {planForm.type === 'SERVICE_PACKAGE' && (
                <Field label="Total Sessions" icon={Activity} error={errors.totalSessions} required>
                  <input type="number" required placeholder="e.g. 5" value={planForm.totalSessions} onChange={e => {
                    setPlanForm({...planForm, totalSessions: e.target.value});
                    if (errors.totalSessions) setErrors({...errors, totalSessions: undefined});
                  }} />
                </Field>
              )}
              {planForm.type === 'WALLET_CREDIT' && (
                <div className="space-y-1">
                  <Field label="Bonus Credit (₹)" icon={DollarSign} error={errors.bonusCredit} required>
                    <input type="number" step="0.01" required placeholder="e.g. 200" value={planForm.bonusCredit} onChange={e => {
                      setPlanForm({...planForm, bonusCredit: e.target.value});
                      if (errors.bonusCredit) setErrors({...errors, bonusCredit: undefined});
                    }} />
                  </Field>
                  <p className="text-[11px] font-medium text-emerald-600 mt-2 pl-1">Total wallet credit given to customer: ₹{Number(planForm.price) + Number(planForm.bonusCredit)}</p>
                </div>
              )}
            </div>
          </div>

          <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0" style={{ background: 'var(--theme-secondary)' }}>
            <button type="button" onClick={() => setViewState('list')} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
            <button type="button" onClick={handleSavePlan} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: 'var(--theme-primary)' }}>
              {editItem ? 'Save plan' : 'Create plan'}
            </button>
          </footer>
        </SideDrawer>
      )}

      {/* Ledger Inline Form */}
      {viewState === 'ledger_form' && (
        <SideDrawer onClose={() => setViewState('list')} maxWidth="max-w-3xl">
          <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: 'var(--theme-primary)' }}>
                <UserCheck size={18} />
              </div>
              <div>
                <h3 className="text-lg font-extrabold text-[#0d0d0d]">{editItem ? 'Edit ledger' : 'Assign membership'}</h3>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Link plan to customer</p>
              </div>
            </div>
            <button type="button" onClick={() => setViewState('list')} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50"><IconX size={16} /></button>
          </header>
          
          <div className="px-6 py-5 flex flex-col gap-4 flex-1 overflow-y-auto custom-scrollbar">
            <Field label="Customer" icon={UserCheck} error={errors.customerId} required>
              <CustomSelect
                value={ledgerForm.customerId}
                onChange={v => {
                  setLedgerForm({...ledgerForm, customerId: v});
                  if (errors.customerId) setErrors({...errors, customerId: undefined});
                }}
                disabled={!!editItem}
                options={[
                  { value: "", label: "Select a customer..." },
                  ...customers.map(c => ({ value: c.id, label: `${c.name} ${c.phone ? `- ${c.phone}` : ''}` }))
                ]}
              />
            </Field>
            <Field label="Select Plan" icon={Award} error={errors.membershipPlanId}>
              <CustomSelect
                value={ledgerForm.membershipPlanId}
                onChange={v => {
                  setLedgerForm({...ledgerForm, membershipPlanId: v});
                  if (errors.membershipPlanId) setErrors({...errors, membershipPlanId: undefined});
                }}
                disabled={!!editItem}
                options={[
                  { value: "", label: "-- Choose a Plan --" },
                  ...plans.map(p => ({ value: p.id, label: `${p.name} (${p.type.replace('_', ' ')})` }))
                ]}
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Start Date" error={errors.startDate} required>
                <CustomDatePickerFixed value={ledgerForm.startDate} onChange={v => {
                  setLedgerForm({...ledgerForm, startDate: v});
                  if (errors.startDate) setErrors({...errors, startDate: undefined});
                }} className={`w-full ${errors.startDate ? '!border-rose-400 focus-within:!border-rose-500' : ''}`} />
              </Field>
              <Field label="Expiry Date" error={errors.expiryDate} required>
                <CustomDatePickerFixed value={ledgerForm.expiryDate} onChange={v => {
                  setLedgerForm({...ledgerForm, expiryDate: v});
                  if (errors.expiryDate) setErrors({...errors, expiryDate: undefined});
                }} className={`w-full ${errors.expiryDate ? '!border-rose-400 focus-within:!border-rose-500' : ''}`} />
              </Field>
            </div>

            {!editItem && (
              <Field label="Starting Balance (Sessions/Credit)" icon={Activity} error={errors.remainingValue} required>
                <input type="number" step="0.01" required placeholder="e.g. 5" value={ledgerForm.remainingValue} onChange={e => {
                  setLedgerForm({...ledgerForm, remainingValue: e.target.value});
                  if (errors.remainingValue) setErrors({...errors, remainingValue: undefined});
                }} />
              </Field>
            )}

            {editItem && (
              <Field label="Remaining Value (Sessions / Credit)" icon={Activity} error={errors.remainingValue} required>
                <input type="number" step="0.01" required placeholder="e.g. 3" value={ledgerForm.remainingValue} onChange={e => {
                  setLedgerForm({...ledgerForm, remainingValue: e.target.value});
                  if (errors.remainingValue) setErrors({...errors, remainingValue: undefined});
                }} />
              </Field>
            )}
          </div>

          <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0" style={{ background: 'var(--theme-secondary)' }}>
            <button type="button" onClick={() => setViewState('list')} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
            <button type="button" onClick={handleSaveLedger} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: 'var(--theme-primary)' }}>
              {editItem ? 'Save ledger' : 'Assign membership'}
            </button>
          </footer>
        </SideDrawer>
      )}
    </div>
  );
};

